import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { SUPABASE_IMAGE_BASE_URL } from '../../lib/constants';
import { ArrowLeft, User, Share2, Repeat, MessageCircle } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Toast } from '../../components/ui/Toast';
import { useCollection } from '../../hooks/useCollection';
import { PageLayout } from '../../components/layout/PageLayout';
import { AdSense } from '../../components/ads/AdSense';
import { useTranslation } from '../../hooks/useTranslation';

interface Trade {
  id: string;
  status: 'pending' | 'accepted' | 'rejected' | 'completed';
  created_at: string;
  user_id: string;
  wanted_card: {
    id: number;
    card_fr: string;
    card_en: string;
    image: string;
    set: string;
    set_info?: {
      display_name: string;
      display_name_en?: string;
      img: string | null;
    };
    rarity: string;
  };
  trade_cards: {
    id: string;
    card: {
      id: number;
      card_fr: string;
      card_en: string;
      image: string;
      set: string;
      set_info?: {
        display_name: string;
        display_name_en?: string;
        img: string | null;
      };
      rarity: string;
    };
  }[];
  creator: {
    username: string;
    avatar_url?: string;
  };
}

interface RawCardData {
  id: number;
  card_fr: string;
  card_en: string;
  image: string;
  set: string;
  set_info?: {
    display_name: string;
    display_name_en?: string;
    img: string | null;
  };
  rarity: string;
}

interface RawTradeCardData {
  id: string;
  card: RawCardData;
}

interface RawCreatorData {
  username: string;
  avatar_url?: string;
}

interface RawTradeData {
  id: string;
  status: 'pending' | 'accepted' | 'rejected' | 'completed';
  created_at: string;
  user_id: string;
  wanted_card: RawCardData;
  trade_cards: RawTradeCardData[];
  creator: RawCreatorData;
}

const getAbsoluteUrl = (path: string) => {
  const baseUrl = import.meta.env.VITE_APP_BASE_URL || 'https://pokemonpocket.fr';
  return `${baseUrl}${path}`;
};

// Fonction pour obtenir l'URL de l'image en fonction de la langue
const getImageUrl = (imagePath: string | null | undefined, language: string) => {
  if (!imagePath) return ''; // Retourner une chaîne vide au lieu de null
  return `${SUPABASE_IMAGE_BASE_URL}${language === 'en' ? 'en/' : ''}${imagePath}`;
};

const getMetaImage = (trade: Trade | null, language: string) => {
  if (!trade) {
    return 'https://pokemonpocket.fr/assets/default-trade-share.jpg';
  }
  const imageUrl = getImageUrl(trade.wanted_card.image, language);
  return `${imageUrl}?width=1200&height=630&fit=contain`;
};

// Ajout de la fonction pour générer les données structurées
const getStructuredData = (trade: Trade | null, lang: string) => {
  if (!trade) return null;

  const cardName = lang === 'fr' ? trade.wanted_card.card_fr : trade.wanted_card.card_en;
  
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": `${cardName} - ${lang === 'fr' ? 'Carte Pokémon à échanger' : 'Pokémon Card for trade'}`,
    "description": `${trade.creator.username} ${lang === 'fr' ? 'propose un échange' : 'offers a trade'}: ${cardName} ${lang === 'fr' ? 'contre une de ces' : 'for one of these'} ${trade.trade_cards.length} ${lang === 'fr' ? 'cartes' : 'cards'}.`,
    "image": getMetaImage(trade, lang),
    "offers": {
      "@type": "Offer",
      "availability": trade.status === 'pending' ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "seller": {
        "@type": "Person",
        "name": trade.creator.username
      }
    },
    "category": lang === 'fr' ? "Cartes Pokémon à échanger" : "Pokémon Cards for trade",
    "brand": {
      "@type": "Brand",
      "name": "Pokémon"
    }
  };
};

const getCardCost = (rarity: string): number => {
  const costMap: { [key: string]: number } = {
    '♢': 0,
    '♢♢': 0,
    '♢♢♢': 120,
    '♢♢♢♢': 500,
    '☆': 400,
    '☆☆': -1, // Non échangeable
    '☆☆☆': -1, // Non échangeable
    '♛': -1, // Non échangeable
    '✧': 600, // Shiny (1 icône) - valeur élevée
    '✧✧': -1  // Shiny (2 icônes) - non échangeable
  };
  return costMap[rarity] || 0;
};

export const TradeInfo = () => {
  const { tradeId } = useParams<{ tradeId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [trade, setTrade] = useState<Trade | null>(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const { collection } = useCollection();
  const { t, lang, getPathForLang } = useTranslation();

  useEffect(() => {
    loadTrade();
  }, [tradeId]);

  const loadTrade = async () => {
    if (!tradeId) return;

    try {
      const { data, error } = await supabase
        .from('trades')
        .select(`
          id,
          status,
          created_at,
          user_id,
          wanted_card:cards!trades_wanted_card_id_fkey(
            id,
            card_fr,
            card_en,
            image,
            set,
            rarity
          ),
          trade_cards:trade_cards!trade_cards_trade_id_fkey(
            id,
            card:cards!trade_cards_card_id_fkey(
              id,
              card_fr,
              card_en,
              image,
              set,
              rarity
            )
          ),
          creator:profiles!trades_user_id_fkey1(
            username,
            avatar_url
          )
        `)
        .eq('id', tradeId)
        .single();

      if (error) throw error;

      const rawData = data as unknown as RawTradeData;

      // Récupérer les informations de set pour la carte voulue et les cartes proposées
      await loadSetInfo(rawData);

      // Transformation finale en type Trade
      const transformedData: Trade = {
        id: rawData.id,
        status: rawData.status,
        created_at: rawData.created_at,
        user_id: rawData.user_id,
        wanted_card: rawData.wanted_card,
        trade_cards: rawData.trade_cards,
        creator: rawData.creator
      };

      setTrade(transformedData);
    } catch (error) {
      console.error('Error loading trade:', error);
      setToast({
        message: "Erreur lors du chargement de l'échange",
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour charger les informations des sets
  const loadSetInfo = async (rawData: RawTradeData) => {
    if (!rawData) return;

    // Récupérer tous les noms de sets uniques
    const setNames = new Set<string>();
    setNames.add(rawData.wanted_card.set);
    rawData.trade_cards.forEach(card => {
      setNames.add(card.card.set);
    });

    try {
      // Requête pour récupérer les infos des sets
      const { data: setsData, error: setsError } = await supabase
        .from('sets')
        .select('name, display_name, display_name_en, img')
        .in('name', Array.from(setNames));

      if (setsError) throw setsError;

      if (setsData) {
        // Map des infos de sets
        const setsMap = new Map();
        setsData.forEach(set => {
          setsMap.set(set.name, {
            display_name: set.display_name || set.name,
            display_name_en: set.display_name_en,
            img: set.img
          });
        });

        // Ajouter les infos de set à chaque carte
        if (setsMap.has(rawData.wanted_card.set)) {
          rawData.wanted_card.set_info = setsMap.get(rawData.wanted_card.set);
        }

        rawData.trade_cards.forEach(tradeCard => {
          if (setsMap.has(tradeCard.card.set)) {
            tradeCard.card.set_info = setsMap.get(tradeCard.card.set);
          }
        });
      }
    } catch (err) {
      console.error('Error loading set info:', err);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.share({
        title: `${t('trade_info.meta_title')} ${trade?.creator.username}`,
        text: t('trade_info.meta_text'),
        url: url
      });
    } catch (error) {
      // Fallback pour copier le lien si le partage natif n'est pas supporté
      navigator.clipboard.writeText(url);
      setToast({
        message: t('trade_info.copied_to_clipboard'),
        type: 'success'
      });
    }
  };

  const handleContactUser = async () => {
    if (!user || !trade) return;

    try {
      // Vérifier si une conversation existe déjà
      const { data: existingConversations, error: conversationsError } = await supabase
        .from('conversation_participants')
        .select(`
          conversation_id,
          conversations!inner(id)
        `)
        .eq('user_id', user.id);

      if (conversationsError) throw conversationsError;

      // Parmi ces conversations, trouver celle qui contient l'autre utilisateur
      const { data: existingConversation } = await supabase
        .from('conversation_participants')
        .select('conversation_id')
        .eq('user_id', trade.user_id)
        .in('conversation_id', existingConversations?.map(c => c.conversation_id) || [])
        .single();

      if (existingConversation) {
        // Si une conversation existe, rediriger vers celle-ci
        navigate(`/messages/${existingConversation.conversation_id}`);
      } else {
        // Créer une nouvelle conversation
        const { data: newConversation, error: createError } = await supabase
          .from('conversations')
          .insert({
            created_at: new Date().toISOString()
          })
          .select()
          .single();

        if (createError) throw createError;

        // Ajouter les participants
        const participants = [
          {
            conversation_id: newConversation.id,
            user_id: user.id
          },
          {
            conversation_id: newConversation.id,
            user_id: trade.user_id
          }
        ];

        const { error: participantsError } = await supabase
          .from('conversation_participants')
          .insert(participants);

        if (participantsError) throw participantsError;

        // Rediriger vers la nouvelle conversation
        navigate(`/messages/${newConversation.id}`);
      }
    } catch (error) {
      console.error('Erreur lors de la gestion de la conversation:', error);
    }
  };

  const getCardName = (card: any) => {
    return lang === 'fr' ? card.card_fr : card.card_en;
  };

  // Fonction pour obtenir le nom affiché du set
  const getSetDisplayName = (card: {
    set: string;
    set_info?: {
      display_name: string;
      display_name_en?: string;
      img: string | null;
    };
  }) => {
    if (card.set_info) {
      return lang === 'en' && card.set_info.display_name_en 
        ? card.set_info.display_name_en 
        : card.set_info.display_name || card.set;
    }
    return card.set;
  };

  // Composant pour afficher l'image du set en plus grand
  const SetImageLarge = ({ card }: { card: { 
    set: string; 
    set_info?: { 
      display_name: string; 
      display_name_en?: string; 
      img: string | null; 
    }; 
  }}) => {
    if (card.set_info?.img) {
      return (
        <div className="mt-4 flex justify-center">
          <div className="bg-gray-100 p-3 rounded-lg">
            <img 
              src={`${SUPABASE_IMAGE_BASE_URL}${lang === 'en' ? 'en/' : ''}${card.set_info.img}`}
              alt={getSetDisplayName(card)}
              className="w-16 h-16 object-contain"
            />
          </div>
        </div>
      );
    }
    
    return null;
  };

  const pageTitle = trade 
    ? t('trade_info.seo.dynamic_title', {
        card: getCardName(trade.wanted_card),
        offered_cards: trade.trade_cards.slice(0, 3).map(tc => getCardName(tc.card)).join(', ') + (trade.trade_cards.length > 3 ? '...' : '')
      })
    : t('trade_info.seo.title');

  const metaDescription = trade
    ? t('trade_info.seo.dynamic_description', {
        username: trade.creator.username,
        card: getCardName(trade.wanted_card),
        count: trade.trade_cards.length
      })
    : t('trade_info.seo.description');

  const currentUrl = getAbsoluteUrl(window.location.pathname);
  const metaImage = getMetaImage(trade, lang);

  useEffect(() => {
    if (trade) {
      const defaultImg = new Image();
      defaultImg.src = 'https://pokemonpocket.fr/assets/default-trade-share.jpg';
      
      const tradeImg = new Image();
      tradeImg.src = getMetaImage(trade, lang);
    }
  }, [trade, lang]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!trade) {
    return (
      <PageLayout
        breadcrumbProps={{
          items: [
            { label: t('trade_info.back_to_trades'), path: getPathForLang('/trades/browse', lang), icon: <Repeat className="w-4 h-4" /> },
            { label: t('trade_info.trade_details'), path: getPathForLang('/trades/not-found', lang) }
          ]
        }}
      >
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-lg p-8 sm:p-12 text-center">
            <div className="flex flex-col items-center justify-center gap-6">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gray-100 flex items-center justify-center">
                <svg className="w-12 h-12 sm:w-14 sm:h-14 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="space-y-3">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('trade_info.trade_not_found')}</h1>
                <p className="text-gray-500 text-base sm:text-lg max-w-md mx-auto">
                  {t('trade_info.trade_not_found_message')}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Link
                  to={getPathForLang('/trades/browse', lang)}
                  className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-md hover:shadow-lg"
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  {t('trade_info.back_to_trades')}
                </Link>
                <Link
                  to={getPathForLang('/trades/create', lang)}
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-gray-700 font-medium rounded-xl border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all duration-300"
                >
                  <Repeat className="w-5 h-5 mr-2" />
                  {t('trade_info.create_trade')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      breadcrumbProps={{
        items: [
          { label: t('trade_info.back_to_trades'), path: getPathForLang('/trades/browse', lang), icon: <Repeat className="w-4 h-4" /> },
          { label: t('trade_info.trade_details'), path: getPathForLang(`/trades/${trade.id}`, lang) }
        ]
      }}
    >
      <Helmet>
        <html lang={lang} />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={currentUrl} />
        
        {/* Open Graph */}
        <meta property="og:type" content="product" />
        <meta property="og:site_name" content="Pokémon Pocket" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image" content={metaImage} />
        <meta property="og:image:secure_url" content={metaImage} />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:alt" content={trade ? t('trade_info.seo.og_image_alt', { card: getCardName(trade.wanted_card) }) : t('trade_info.seo.default_og_image_alt')} />
        <meta property="og:locale" content={lang === 'fr' ? 'fr_FR' : 'en_US'} />
        <meta property="og:site" content="pokemonpocket.fr" />
        <meta property="og:ttl" content="2419200" />
        <meta property="fb:app_id" content="1696986811252257" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@PokemonPocketFR" />
        <meta name="twitter:creator" content="@PokemonPocketFR" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:domain" content="pokemonpocket.fr" />
        <meta name="twitter:image" content={metaImage} />
        <meta name="twitter:image:alt" content={trade ? t('trade_info.seo.twitter_image_alt', { card: getCardName(trade.wanted_card) }) : t('trade_info.seo.default_twitter_image_alt')} />
        
        {/* Métadonnées supplémentaires */}
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Pokémon Pocket" />
        <meta name="keywords" content={t('trade_info.seo.keywords', { 
          card: trade ? getCardName(trade.wanted_card) : '',
          set: trade ? trade.wanted_card.set : '' 
        })} />
        
        {/* Préchargement des images */}
        <link rel="preload" as="image" href={metaImage} />
        
        {/* Données structurées JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(getStructuredData(trade, lang))}
        </script>
        
        {/* Script AdSense */}
        <script async src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-4579146101917687" crossOrigin="anonymous"></script>
      </Helmet>

      <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-lg p-4 sm:p-8 max-w-7xl mx-auto">
        {/* Annonce AdSense en haut de la page - visible sur tous les appareils */}
        <div className="mt-6 mb-8 flex justify-center items-center">
          <AdSense adSlot="1424794127" id="page-top" />
        </div>
        
        {/* En-tête de l'échange */}
        <div className="mb-6 sm:mb-8 border-b border-gray-200 pb-6">
          <div className="flex items-start sm:items-center gap-3 sm:gap-4">
            {trade.creator.avatar_url ? (
              <Link 
                to={getPathForLang(`/users/${trade.creator.username}`, lang)}
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-full overflow-hidden ring-2 ring-blue-500 ring-offset-2 flex-shrink-0 hover:ring-blue-600 transition-all"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={trade.creator.avatar_url}
                  alt={trade.creator.username}
                  className="w-full h-full object-cover"
                />
              </Link>
            ) : (
              <Link 
                to={getPathForLang(`/users/${trade.creator.username}`, lang)}
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white text-lg font-bold flex-shrink-0 hover:from-blue-600 hover:to-indigo-600 transition-all"
                onClick={(e) => e.stopPropagation()}
              >
                {trade.creator.username.charAt(0).toUpperCase()}
              </Link>
            )}
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-3xl font-bold text-gray-900 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                <span className="truncate">{t('trade_info.trade_proposed_by')}</span>{' '}
                <Link 
                  to={getPathForLang(`/users/${trade.creator.username}`, lang)}
                  className="text-blue-600 truncate hover:text-blue-700 hover:underline transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  {trade.creator.username}
                </Link>
              </h1>
              <p className="text-sm sm:text-base text-gray-500 mt-1 truncate">
                {t('trade_info.created_on')} {new Date(trade.created_at).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 sm:gap-12">
          {/* Carte recherchée */}
          <div className="lg:w-1/3">
            <div className="sticky top-8 space-y-6">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 sm:p-6">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6 flex items-center">
                  <span className="w-7 h-7 sm:w-8 sm:h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mr-2 sm:mr-3 text-sm font-bold flex-shrink-0">1</span>
                  <span className="truncate">{t('trade_info.wanted_card')}</span>
                </h2>
                <div className="relative aspect-square rounded-lg overflow-hidden shadow-sm bg-white">
                  <img
                    src={getImageUrl(trade.wanted_card.image, lang)}
                    alt={getCardName(trade.wanted_card)}
                    className="w-full h-full object-contain p-4"
                  />
                </div>
                <div className="mt-4 text-center">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {getCardName(trade.wanted_card)}
                  </h3>
                  <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-700">
                    <span className="text-sm font-medium">{getSetDisplayName(trade.wanted_card)} • {trade.wanted_card.rarity}</span>
                  </div>
                  <SetImageLarge card={trade.wanted_card} />
                  {user && (
                    <p className="mt-3 text-sm text-gray-600">
                      {collection[trade.wanted_card.id]?.quantity || 0} {t('trade_info.specimens_in_collection')}
                    </p>
                  )}
                </div>
              </div>

              {/* Informations détaillées */}
              <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="truncate">{t('trade_info.trade_details')}</span>
                </h3>
                <div className="space-y-4 divide-y divide-gray-100">
                  <div className="flex justify-between items-center py-2 gap-4">
                    <span className="text-sm font-medium text-gray-500 flex-shrink-0">{t('trade_info.status')}</span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium truncate ${
                      trade.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      trade.status === 'accepted' ? 'bg-green-100 text-green-800' :
                      trade.status === 'rejected' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {trade.status === 'pending' && t('trade_info.status_pending')}
                      {trade.status === 'accepted' && t('trade_info.status_accepted')}
                      {trade.status === 'rejected' && t('trade_info.status_rejected')}
                      {trade.status === 'completed' && t('trade_info.status_completed')}
                    </span>
                  </div>
                  <div className="pt-4">
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                        <img 
                          src="https://blog.pokemonpocket.fr/wp-content/uploads/2025/01/piece-echange.png"
                          alt="Jeton d'échange"
                          className="w-5 h-5 mr-2"
                        />
                        <span>{t('trade_info.exchange_cost')}</span>
                      </h4>
                      <div className="flex items-center justify-center bg-white rounded-lg p-3">
                        <span className="text-lg font-medium text-gray-900">{getCardCost(trade.wanted_card.rarity)}</span>
                        <img 
                          src="https://blog.pokemonpocket.fr/wp-content/uploads/2025/01/piece-echange.png"
                          alt="Jetons"
                          className="w-5 h-5 ml-2"
                        />
                      </div>
                      <div className="mt-3">
                        <p className="text-xs text-gray-500">
                          {t('trade_info.token_scale')}
                        </p>
                      </div>
                    </div>
                  </div>
                  {user && user.id !== trade.user_id && (
                    <div className="pt-4">
                      <button
                        onClick={handleContactUser}
                        className="w-full inline-flex justify-center items-center px-4 py-2 text-blue-600 bg-white border-2 border-blue-200 rounded-lg hover:bg-blue-50 transition-all duration-300 text-sm font-medium mb-2"
                      >
                        <MessageCircle className="w-5 h-5 mr-2" />
                        {t('trade_info.contact_creator')}
                      </button>
                      
                      <Link
                        to={getPathForLang(`/users/${trade.creator.username}`, lang)}
                        className="w-full inline-flex justify-center items-center px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all duration-300 text-sm font-medium"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <User className="w-5 h-5 mr-2" />
                        {t('trade_info.view_profile')}
                      </Link>
                    </div>
                  )}
                  
                  {(!user || (user && user.id === trade.user_id)) && (
                    <div className="pt-4">
                      <Link
                        to={getPathForLang(`/users/${trade.creator.username}`, lang)}
                        className="w-full inline-flex justify-center items-center px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all duration-300 text-sm font-medium"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <User className="w-5 h-5 mr-2" />
                        {t('trade_info.view_profile')}
                      </Link>
                    </div>
                  )}
                </div>
              </div>

              {/* Section de partage */}
              <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Share2 className="w-5 h-5 mr-2 text-blue-500 flex-shrink-0" />
                  <span className="truncate">{t('trade_info.share_trade')}</span>
                </h3>
                <div className="space-y-3">
                  <button
                    onClick={() => {
                      const url = window.location.href;
                      const title = `Échange : ${getCardName(trade.wanted_card)}`;
                      window.open(
                        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
                        'facebook-share-dialog',
                        'width=800,height=600'
                      );
                    }}
                    className="w-full px-4 py-2 bg-[#1877F2] text-white rounded-lg hover:bg-[#166FE5] transition-colors flex items-center justify-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
                    </svg>
                    Facebook
                  </button>
                  <button
                    onClick={() => {
                      const url = window.location.href;
                      const text = `🔄 Échange Pokémon !\n\n${trade.creator.username} propose sa carte ${getCardName(trade.wanted_card)}\n\n#PokemonTCG #Pokemon`;
                      window.open(
                        `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
                        'twitter-share-dialog',
                        'width=800,height=600'
                      );
                    }}
                    className="w-full px-4 py-2 bg-[#1DA1F2] text-white rounded-lg hover:bg-[#1A91DA] transition-colors flex items-center justify-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                    </svg>
                    Twitter
                  </button>
                  <button
                    onClick={() => {
                      const url = window.location.href;
                      navigator.clipboard.writeText(url);
                      setToast({
                        message: t('trade_info.copied_to_clipboard'),
                        type: 'success'
                      });
                    }}
                    className="w-full px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <Share2 className="w-5 h-5" />
                    {t('trade_info.copy_link')}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Cartes proposées */}
          <div className="lg:w-2/3">
            <div className="space-y-6 sm:space-y-8">
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6 flex items-center">
                  <span className="w-7 h-7 sm:w-8 sm:h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mr-2 sm:mr-3 text-sm font-bold flex-shrink-0">2</span>
                  <span className="truncate">{t('trade_info.offered_cards')}</span>
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {trade.trade_cards.map((tradeCard) => (
                    <div
                      key={tradeCard.id}
                      className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow border border-gray-100 group"
                    >
                      <div className="relative aspect-square">
                        <img
                          src={getImageUrl(tradeCard.card.image, lang)}
                          alt={getCardName(tradeCard.card)}
                          className={`w-full h-full object-contain p-4 transition-transform group-hover:scale-105 ${
                            user && (!collection[tradeCard.card.id]?.quantity) ? 'grayscale' : ''
                          }`}
                        />
                        {user && (
                          <div className="absolute top-2 right-2">
                            <div className="bg-blue-600 text-white text-sm font-bold rounded-full w-8 h-8 flex items-center justify-center shadow-lg">
                              {collection[tradeCard.card.id]?.quantity || 0}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="p-3 sm:p-4">
                        <h3 className="font-semibold text-gray-900 mb-1 truncate">
                          {getCardName(tradeCard.card)}
                        </h3>
                        <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 truncate max-w-full">
                          {getSetDisplayName(tradeCard.card)} • {tradeCard.card.rarity}
                        </div>
                        <SetImageLarge card={tradeCard.card} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Section d'informations sur l'échange */}
              <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="truncate">{t('trade_info.about_this_trade')}</span>
                </h3>
                <div className="prose prose-blue max-w-none">
                  <div className="flex flex-col gap-2">
                    <p className="text-sm sm:text-base text-gray-700">
                      {t('trade_info.trade_proposed_by')}{' '}
                      <Link 
                        to={getPathForLang(`/users/${trade.creator.username}`, lang)}
                        className="inline-flex items-center text-blue-600 hover:text-blue-700 hover:underline transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <strong className="truncate max-w-[150px]">{trade.creator.username}</strong>
                      </Link>
                    </p>
                    <p className="text-sm sm:text-base text-gray-700">
                      {t('trade_info.offers_cards_for', { count: trade.trade_cards.length })}{' '}
                      <strong className="text-blue-600 break-words">{getCardName(trade.wanted_card)}</strong>
                    </p>
                  </div>
                  
                  {/* Annonce AdSense */}
                  <div className="mt-6 mb-8 flex justify-center items-center">
                    <AdSense adSlot="1424794127" id="content-middle" />
                  </div>
                  
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 sm:p-6 my-6">
                    <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      <span className="truncate">{t('trade_info.how_trade_works')}</span>
                    </h4>
                    <ol className="list-none space-y-4 text-sm sm:text-base text-gray-700">
                      <li className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-semibold text-sm">1</span>
                        <span>{t('trade_info.step1')} <span className="text-blue-600 break-words font-medium">{getCardName(trade.wanted_card)}</span></span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-semibold text-sm">2</span>
                        <span>{t('trade_info.step2')}</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-semibold text-sm">3</span>
                        <span>{t('trade_info.step3')}</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-semibold text-sm">4</span>
                        <span>{t('trade_info.step4')}</span>
                      </li>
                    </ol>
                  </div>

                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-xl">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm sm:text-base text-yellow-800">
                          <strong className="font-medium block mb-1">{t('trade_info.important_note')}</strong>
                          <span className="block text-sm leading-relaxed">
                            {t('trade_info.token_warning')}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {user && trade.status === 'pending' && user.id !== trade.user_id && (
          <div className="mt-8 sm:mt-10 pt-6 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <p className="text-sm sm:text-base text-gray-500 italic text-center sm:text-left">
                {t('trade_info.accept_terms')}
              </p>
              <Link
                to={getPathForLang(`/trades/${trade.id}/accept`, lang)}
                className="w-full sm:w-auto inline-flex items-center justify-center px-6 sm:px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-base sm:text-lg font-medium rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
              >
                <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                <span className="truncate">{t('trade_info.accept_trade')}</span>
              </Link>
            </div>
          </div>
        )}

        {trade.status !== 'pending' && (
          <div className="mt-8 sm:mt-10 pt-6 border-t border-gray-200">
            <div className="bg-gray-50 rounded-xl p-4 sm:p-6 text-center">
              <div className="flex items-center justify-center mb-4">
                <svg className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('trade_info.trade_unavailable')}
              </h3>
              <p className="text-gray-600">
                {t('trade_info.already_completed')}
              </p>
            </div>
          </div>
        )}

        {!user && trade.status === 'pending' && (
          <div className="mt-8 sm:mt-10 pt-6 border-t border-gray-200">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 sm:p-6 text-center">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
                {t('trade_info.login_to_participate')}
              </h3>
              <p className="text-sm sm:text-base text-gray-600 mb-6">
                {t('trade_info.login_required')}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                <Link
                  to={getPathForLang('/login', lang)}
                  className="inline-flex items-center justify-center px-4 sm:px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <User className="w-5 h-5 mr-2 flex-shrink-0" />
                  <span className="truncate">{t('trade_info.login')}</span>
                </Link>
                <Link
                  to={getPathForLang('/register', lang)}
                  className="inline-flex items-center justify-center px-4 sm:px-6 py-2 bg-white text-gray-700 font-medium rounded-lg border border-gray-300 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                >
                  <span className="truncate">{t('trade_info.create_account')}</span>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Annonce AdSense en bas de page - visible sur tous les appareils */}
      <div className="mt-6 mb-8 flex justify-center items-center">
        <AdSense adSlot="1424794127" id="page-bottom" />
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </PageLayout>
  );
}; 